import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import _isEmpty from "lodash/isEmpty"
import { useRouter } from "next/router"
import { getConfig } from "@/constants/config"
import {
  convertStringToBoolean,
  getOffsetTop,
  getShortenedUrlwithGeneral,
  isAuthenticated,
} from "@/utils/helper"
import {
  selectAuthState,
  setAuthModalVisibility,
  setHideCustomerSignupCTA,
} from "@/store/features/authSlice"
import { selectGenericState } from "@/store/features/genericSlice"
import {
  DEFAULT_SCROLL,
  FIND_A_PRO_HASH,
  MY_ACCOUNT_PATH,
  INSTALLATION_SERVICES_HASH,
  GATED_HASHES,
  MORE_OPTIONS,
} from "@/constants"
import { addTeaserActionAnalytics } from "@/components/Default/Teaser/v1/analytics"
import { useConfig } from "@/hooks/useConfig"

const Action = props => {
  const {
    primaryCTA = "",
    secondaryCTA = "",
    playVideoCTA = "",
    videoTitle = "",
    mainTitle = "",
    link = "",
    text = "",
    newTab = "",
    urlRef = "",
    id = "",
    pageType = "",
    linkAll = "",
    componentPosition = null,
    eventName = null,
    eventAction = null,
    dataLayerLinkType = `${pageType}:${mainTitle}`,
    productCategory = null,
    productCollectionsName = null,
    productRoom = null,
    propertiesPageType = "",
    playVideoCTAcb = () => "",
    index = 0,
    activeIndex = 0,
    setActive = () => "",
    item,
    bannerIndex = -1,
  } = props
  const { scroll, findAPro, installationServices } = DEFAULT_SCROLL
  const generalConfigData = useConfig()
  const shortLink = getShortenedUrlwithGeneral(link, generalConfigData)
  const [general, setGeneral] = useState(generalConfigData)

  const dispatch = useDispatch()
  const router = useRouter()
  const { isAuth } = useSelector(selectAuthState)
  const { pageType: datalayerPageType } = useSelector(selectGenericState)
  const [actionLink, setActionLink] = useState(shortLink)
  const [target, setTarget] = useState("")
  const [loginRegisterCallback, setLoginRegisterCallback] = useState(null)

  useEffect(() => {
    if (
      loginRegisterCallback &&
      isAuth &&
      router.pathname.includes(MY_ACCOUNT_PATH)
    ) {
      handleOnLinkClick(loginRegisterCallback)
    }
  }, [loginRegisterCallback, isAuth])

  useEffect(() => {
    if (_isEmpty(generalConfigData)) {
      getConfig().then(async config => {
        setGeneral(config?.general)
      })
    }
  }, [])

  useEffect(() => {
    if (!_isEmpty(link) && !_isEmpty(general)) {
      const actLink = getShortenedUrlwithGeneral(link, general)
      setActionLink(actLink)
    }
  }, [link, general])

  useEffect(() => {
    if (!general) return

    const hash = window.location.hash
    if (!hash) return
    if (window.__gbhActionHashScrollHandled === hash) return

    const hashValue = hash.startsWith("#") ? hash.slice(1) : hash
    let hashTarget = document?.getElementById(hashValue)
    if (!hashTarget && globalThis.CSS?.escape) {
      hashTarget = document?.querySelector(
        `#${globalThis.CSS.escape(hashValue)}`
      )
    }

    if (
      hashTarget?.classList?.contains("cmp-tabs__tab") ||
      hashTarget?.closest(".cmp-tabs")
    ) {
      return
    }
    const timeoutId = setTimeout(() => {
      exploreMoreScroll(hash)
      window.__gbhActionHashScrollHandled = hash
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [general])

  const handleLogin = e => {
    if (router.pathname.includes(MY_ACCOUNT_PATH)) {
      e.preventDefault()
      e.stopPropagation()
      window.loginRegisterCallback = () => {
        setLoginRegisterCallback(e)
        if (actionLink) router.push(actionLink)
      }
      dispatch(setAuthModalVisibility({ show: true }))
    } else {
      handleOnLinkClick()
    }
  }

  const handleOnLinkClick = event => {
    return propertiesPageType === "modalWindow" && playVideoCTA
      ? playVideoCTAcb(event)
      : undefined
  }

  /**
   * Handles click events for action links within the Teaser component.
   *
   * - Handles hash links (e.g., `#section`) using Next.js shallow routing to prevent page refresh.
   * - Handles special KPN hash links (e.g., `#KPN::url`) for authentication flows and dispatches related actions.
   * - Sets the active teaser index and component info for UI state.
   * - Triggers login if the user is not authenticated and is on the "My Account" page.
   * - Calls `handleOnLinkClick` for non-hash links or specific hash links.
   * - Sends analytics data for the teaser action.
   *
   * @param {React.MouseEvent} event - The click event triggered by the user.
   */
  const handleOnClick = event => {
    const actionLink = event?.target?.getAttribute("href") ?? ""
    const componentInfo = event.target.getAttribute("data-gbh-component")

    const teaserAnalyticsData = {
      pageType,
      componentPosition,
      videoTitle,
      link,
      text,
      eventName,
      dataLayerLinkType,
      eventAction,
      productCategory,
      productCollectionsName,
      productRoom,
      datalayerPageType,
      mainTitle,
      componentInfo,
    }
    // Handle gated hash links (e.g. #signin, #login, #signup) — show auth modal directly
    if (GATED_HASHES.some(hash => actionLink?.startsWith(hash))) {
      event.preventDefault()
      const active =
        actionLink.includes("#signup") || actionLink.includes("#createaccount")
          ? 1
          : 0
      dispatch(setAuthModalVisibility({ show: true, active }))
      addTeaserActionAnalytics(teaserAnalyticsData)
      return
    }

    // Handle hash links to prevent page refresh
    if (
      actionLink?.startsWith("#") &&
      event.target.text !== MORE_OPTIONS.WATCH_VIDEO &&
      !actionLink?.startsWith("#KPN")
    ) {
      // Update URL using Next.js router (shallow routing)
      router.push(`${window.location.pathname}${actionLink}`, undefined, {
        shallow: true,
      })

      exploreMoreScroll(event)
    }

    if (actionLink?.startsWith("#KPN")) {
      event.preventDefault()
      event.stopPropagation()
      const link = actionLink.split("::")
      const id = link[0]
      const url = link[1]
      if (link) {
        dispatch(setHideCustomerSignupCTA(true))
        if (id?.includes("signIn")) {
          window.KPNSignIn = true
          isAuthenticated(url, 0, true)
        } else if (id?.includes("createAccount")) {
          window.KPNSignIn = false
          isAuthenticated(url, 1, true)
        } else {
          isAuthenticated(url)
        }
      }
    }

    const currentIndex = Number(event.target.getAttribute("data-index")) || 0
    setActive(currentIndex)

    if (router.pathname.includes(MY_ACCOUNT_PATH) && !isAuth) {
      handleLogin(event)
    } else if (
      actionLink?.startsWith("#") &&
      Object.values(MORE_OPTIONS).includes(event.target.text)
    ) {
      handleOnLinkClick(event)
    } else if (!actionLink?.startsWith("#")) {
      // Only call handleOnLinkClick for non-hash links
      handleOnLinkClick(event)
    }

    addTeaserActionAnalytics(teaserAnalyticsData)
  }

  const exploreMoreScroll = e => {
    let hash

    if (typeof e === "string") {
      hash = e
    } else if (e?.preventDefault) {
      e.preventDefault()
      hash = e.target.hash || e.target.getAttribute("href")
    }

    if (!hash || hash === "#") return

    const rawHash = hash.startsWith("#") ? hash.slice(1) : hash
    let decodedHash = rawHash
    try {
      decodedHash = decodeURIComponent(rawHash)
    } catch (error) {
      console.warn("Unable to decode hash value", error)
      decodedHash = rawHash
    }
    const normalizedHash = `#${decodedHash}`

    // Use getElementById first so hashes with special CSS selector chars (e.g. '&') are supported.
    let element = document?.getElementById(decodedHash)
    if (!element && globalThis.CSS?.escape) {
      element = document?.querySelector(
        `#${globalThis.CSS.escape(decodedHash)}`
      )
    }

    if (!element) {
      console.warn(`Element with selector "${normalizedHash}" not found`)
      return
    }

    let offset = scroll
    if (normalizedHash === FIND_A_PRO_HASH) {
      offset = findAPro
    } else if (normalizedHash === INSTALLATION_SERVICES_HASH) {
      offset = installationServices
    }

    const offsetTop = getOffsetTop(element) - offset
    window.scrollTo({
      top: offsetTop,
      behavior: "smooth",
    })
  }

  // Added to fix eslint error Disallow target="_blank" attribute without rel="noreferrer" (react/jsx-no-target-blank)
  useEffect(() => {
    if (newTab === false || newTab === "false") {
      setTarget("_self")
    } else {
      setTarget("_blank")
    }
  }, [newTab])

  const getComponentMessage = (message = "") => {
    if (message && bannerIndex > -1) {
      const obj = JSON.parse(message)
      obj.componentInfo.componentMessage = `${
        obj.componentInfo.componentMessage
      }${bannerIndex + 1}`
      return JSON.stringify(obj)
    }
  }

  if (!linkAll) {
    return (
      <a
        className={`cmp-teaser__action-link ${
          convertStringToBoolean(primaryCTA) ? "gbh-primary-button " : ""
        }${
          convertStringToBoolean(secondaryCTA) ? "gbh-secondary-button " : ""
        }${
          propertiesPageType === "modalWindow" && playVideoCTA
            ? "video-play-button "
            : ""
        } ${index === activeIndex ? "active" : ""}`}
        role={actionLink ? "link" : "button"}
        href={actionLink}
        aria-label={`${text} related to ${mainTitle}`}
        target={target}
        rel={urlRef ? "noreferrer" : ""}
        onClick={handleOnClick}
        data-index={index}
        data-gbh-component={getComponentMessage(item?.componentInfos)}
      >
        {text}
      </a>
    )
  }

  return (
    <div
      className={`cmp-teaser__action-link ${
        convertStringToBoolean(primaryCTA) ? "gbh-primary-button " : ""
      }${convertStringToBoolean(secondaryCTA) ? "gbh-secondary-button " : ""}${
        pageType === "modalWindow" && playVideoCTA ? "video-play-button " : ""
      }`}
      role="button"
      aria-describedby={id || ""}
      rel={urlRef ? "noreferrer" : ""}
      aria-label={text}
    >
      {text}
    </div>
  )
}

export default Action
